import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Loader2, Users, UserPlus, Flame, MapPin,
  ChevronRight, Bell
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { NavBar } from '../components/NavBar';
import { getImageUrl } from '../utils/imageUtils';
import { BuddyList } from '../components/buddies/BuddyList';
import { BuddyRequests } from '../components/buddies/BuddyRequests';

const NUDGE_MESSAGES = [
  "Time to get moving!",
  "Let's keep that streak going!",
  "You got this!",
  "Ready to crush it today?",
  "Don't break the chain!"
];

export const BuddiesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [buddies, setBuddies] = useState([]);
  const [myStreak, setMyStreak] = useState(null);
  const [maxBuddies, setMaxBuddies] = useState(5);
  const [suggestions, setSuggestions] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('buddies');
  const [sendingRequest, setSendingRequest] = useState(null);
  const [nudgingBuddy, setNudgingBuddy] = useState(null);
  const [goingLiveBuddyId, setGoingLiveBuddyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [buddiesRes, suggestionsRes, incomingRes, outgoingRes, notifsRes] = await Promise.allSettled([
        api().get('/buddies/'),
        api().get('/buddies/suggestions'),
        api().get('/buddies/requests/incoming'),
        api().get('/buddies/requests/outgoing'),
        api().get('/buddies/notifications')
      ]);
      
      if (buddiesRes.status === 'fulfilled') {
        setBuddies(buddiesRes.value.data.buddies || []);
        setMyStreak(buddiesRes.value.data.my_streak);
        setMaxBuddies(buddiesRes.value.data.max_buddies || 5);
      }
      if (suggestionsRes.status === 'fulfilled') setSuggestions(suggestionsRes.value.data.suggestions || []);
      if (incomingRes.status === 'fulfilled') setIncomingRequests(incomingRes.value.data.requests || []);
      if (outgoingRes.status === 'fulfilled') setOutgoingRequests(outgoingRes.value.data.requests || []);
      if (notifsRes.status === 'fulfilled') setNotifications(notifsRes.value.data.notifications || []);
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleSendRequest = async (userId) => {
    setSendingRequest(userId);
    try {
      await api().post('/buddies/request', { user_id: userId });
      toast.success('Buddy request sent!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    }
    setSendingRequest(null);
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await api().post(`/buddies/requests/${requestId}/accept`);
      toast.success('Buddy request accepted!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await api().post(`/buddies/requests/${requestId}/reject`);
      toast.success('Request declined');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleSendNudge = async (buddyId) => {
    setNudgingBuddy(buddyId);
    try {
      const msg = NUDGE_MESSAGES[Math.floor(Math.random() * NUDGE_MESSAGES.length)];
      await api().post(`/buddies/${buddyId}/nudge`, { message: msg });
      toast.success('Nudge sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send nudge');
    }
    setNudgingBuddy(null);
  };

  const handleGoLive = async (buddy) => {
    setGoingLiveBuddyId(buddy.id);
    try {
      const res = await api().post('/buddies/live-invite', { buddy_id: buddy.id });
      toast.success(`Live invite sent to ${buddy.name?.split(' ')[0] || 'your buddy'}!`);
      navigate(`/live-workout/${res.data.session_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start live workout');
    }
    setGoingLiveBuddyId(null);
  };

  const tabs = [
    { id: 'buddies', label: 'My Buddies', count: buddies.length },
    { id: 'find', label: 'Find', count: suggestions.length },
    { id: 'requests', label: 'Requests', count: incomingRequests.length },
    { id: 'notifications', label: 'Activity', count: notifications.length }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar title="Buddies" />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="buddies-page">
      <NavBar title="Workout Buddies" />
      <main className="container mx-auto px-6 py-8">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Users className="h-6 w-6 text-primary" />
            <span className="text-primary font-heading text-xl tracking-widest uppercase">Accountability</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-4">
            Workout <span className="text-primary">Buddies</span>
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Partner up with others to stay motivated. Share streaks, check-ins, and encourage each other!
          </p>
        </motion.div>

        {/* My Streak Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-primary/20 to-orange-500/20 border border-primary/30 p-6 mb-8 max-w-md mx-auto text-center">
          <Flame className="h-8 w-8 text-orange-400 mx-auto mb-2" />
          <p className="font-heading text-4xl text-white">{myStreak?.current_streak || 0}</p>
          <p className="text-muted-foreground">Day Streak</p>
          <p className="text-xs text-muted-foreground mt-1">Best: {myStreak?.longest_streak || 0} days</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map(tab => (
            <Button key={tab.id} onClick={() => setActiveTab(tab.id)} variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id ? 'bg-primary text-black' : ''}>
              {tab.label}
              {tab.count > 0 && <Badge className="ml-2 bg-white/20 text-current">{tab.count}</Badge>}
            </Button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'buddies' && (
            <BuddyList
              buddies={buddies}
              maxBuddies={maxBuddies}
              nudgingBuddy={nudgingBuddy}
              onNudge={handleSendNudge}
              onSwitchTab={setActiveTab}
              onGoLive={handleGoLive}
              goingLiveBuddyId={goingLiveBuddyId}
            />
          )}

          {activeTab === 'find' && (
            <motion.div key="find" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4 text-center">Suggested Buddies</h3>
              {suggestions.length === 0 ? (
                <div className="text-center py-16 bg-card border border-border max-w-md mx-auto">
                  <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white mb-2">No Suggestions Yet</h3>
                  <p className="text-muted-foreground mb-4">Follow people and have them follow you back to add them as buddies!</p>
                  <Link to="/social"><Button className="bg-primary text-black">Find People to Follow</Button></Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {suggestions.map((user, i) => (
                    <motion.div key={user.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-card border border-border p-6">
                      <div className="flex flex-col items-center text-center">
                        <Link to={`/user/${user.id}`}>
                          <Avatar className="h-16 w-16 mb-3 border-2 border-border">
                            <AvatarImage src={getImageUrl(user.profile_picture)} />
                            <AvatarFallback className="bg-primary/20 text-primary text-xl">{user.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <Link to={`/user/${user.id}`} className="font-heading text-lg text-white hover:text-primary">{user.name}</Link>
                        {user.match_reason && <Badge className="mt-2 bg-green-500/20 text-green-400">{user.match_reason}</Badge>}
                        {user.location?.city && <p className="text-muted-foreground text-sm mt-2 flex items-center gap-1"><MapPin className="h-3 w-3" />{user.location.city}</p>}
                        <div className="flex items-center gap-1 mt-2 text-orange-400">
                          <Flame className="h-4 w-4" /><span>{user.streak?.current_streak || 0} day streak</span>
                        </div>
                        <Button onClick={() => handleSendRequest(user.id)} disabled={sendingRequest === user.id || buddies.length >= maxBuddies} className="mt-4 w-full bg-primary text-black" data-testid={`add-buddy-${user.id}`}>
                          {sendingRequest === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" />Add Buddy</>}
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
              <div className="text-center mt-8">
                <Link to="/discover"><Button variant="outline">Browse All Users<ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
              </div>
            </motion.div>
          )}

          {activeTab === 'requests' && (
            <BuddyRequests incomingRequests={incomingRequests} outgoingRequests={outgoingRequests} onAccept={handleAcceptRequest} onReject={handleRejectRequest} />
          )}

          {activeTab === 'notifications' && (
            <motion.div key="notifications" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-2xl mx-auto">
              {notifications.length === 0 ? (
                <div className="text-center py-16 bg-card border border-border">
                  <Bell className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white mb-2">No Activity Yet</h3>
                  <p className="text-muted-foreground">Nudges and milestone celebrations will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map(notif => (
                    <div key={notif.id} className="bg-card border border-border p-4 flex items-start gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getImageUrl(notif.from_user?.profile_picture)} />
                        <AvatarFallback className="bg-primary/20 text-primary">{notif.from_user?.name?.charAt(0) || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-white">
                          <span className="font-medium">{notif.from_user?.name || 'Someone'}</span>
                          {notif.type === 'buddy_nudge' && <span className="text-muted-foreground"> sent you a nudge:</span>}
                          {notif.type === 'buddy_milestone' && <span className="text-muted-foreground"> hit a milestone!</span>}
                        </p>
                        {notif.message && <p className="text-primary mt-1">{notif.message}</p>}
                        {notif.milestone && <Badge className="mt-2 bg-primary/20 text-primary">{notif.milestone.icon} {notif.milestone.name}</Badge>}
                        <p className="text-xs text-muted-foreground mt-2">{new Date(notif.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default BuddiesPage;
